'use client';

import { useEffect, useRef } from 'react';
import { injectGrumftScriptIntoSlot } from './grumft-iab-ad';
import {
	AD_DEFER_IDLE_FOOTER_MS,
	scheduleDeferredAdLoad,
} from '../../../lib/ad-script-defer';
import {
	GAM_ZONES,
	GRUMFT_STICK_FOOTER_SRC,
	isGrumftScriptTagsEnabled,
} from '../../../lib/grumft-iab-presets';
import { showGamSlot } from '../../../lib/gam-gpt';

const GMFT_APP_UA = /GMFT-App-24978314/;
const MQ = '(max-width: 767px)';
const FOOTER_AD_ROOT_MARGIN = '0px 0px 50000px 0px';

function isFooterMobileViewport() {
	if (typeof window === 'undefined') return false;
	if (typeof window.matchMedia === 'function') {
		return window.matchMedia(MQ).matches;
	}
	return window.innerWidth < 768;
}

/**
 * Banner Grumft sticky acima do footer (mobile) — port do tvfoco-frontend.
 * A barra fixa vai no body, como o script deles fazia, então aparece também nas matérias.
 */
function showGamStickyFooter() {
	const bar = document.createElement('div');
	bar.className = 'gam-sticky-footer';
	document.body.appendChild(bar);
	const gam = showGamSlot(bar, GAM_ZONES.STICK_320x50_4);
	return {
		loaded: gam.loaded,
		destroy: () => {
			gam.destroy();
			bar.remove();
		},
	};
}

export default function GrumftBannerFooterFixed({ loadTrigger = 'scroll' }) {
	const rootRef = useRef(null);

	useEffect(() => {
		const el = rootRef.current;
		if (!el) return undefined;

		let cancelDefer = () => {};
		let nearFooter = false;
		let stickInjected = false;
		let gamSticky = null;

		const scheduleLoad = () => {
			cancelDefer();
			cancelDefer = scheduleDeferredAdLoad(
				() => {
					if (stickInjected) return;
					const anchor = rootRef.current;
					if (!anchor) return;
					stickInjected = true;
					if (!isGrumftScriptTagsEnabled()) {
						gamSticky = showGamStickyFooter();
						gamSticky.loaded.catch(() => {
							gamSticky?.destroy();
							gamSticky = null;
							stickInjected = false;
						});
						return;
					}
					injectGrumftScriptIntoSlot(GRUMFT_STICK_FOOTER_SRC, anchor).loaded.catch(
						() => {
							stickInjected = false;
						}
					);
				},
				{ idleTimeout: AD_DEFER_IDLE_FOOTER_MS }
			);
		};

		const tryScheduleLoad = () => {
			if (GMFT_APP_UA.test(navigator.userAgent)) return;
			if (!isFooterMobileViewport()) return;
			if (!nearFooter) return;
			scheduleLoad();
		};

		if (loadTrigger === 'scroll') {
			const listenerOpts = { passive: true };
			const onFirstScroll = () => {
				window.removeEventListener('scroll', onFirstScroll, listenerOpts);
				nearFooter = true;
				tryScheduleLoad();
			};

			if (window.scrollY > 0 || document.documentElement.scrollTop > 0) {
				nearFooter = true;
				tryScheduleLoad();
			} else {
				window.addEventListener('scroll', onFirstScroll, listenerOpts);
			}

			const mq =
				typeof window.matchMedia === 'function' ? window.matchMedia(MQ) : null;
			const onViewportChange = () => tryScheduleLoad();
			if (mq?.addEventListener) mq.addEventListener('change', onViewportChange);
			else window.addEventListener('resize', onViewportChange);
			window.addEventListener('orientationchange', onViewportChange);

			return () => {
				cancelDefer();
				gamSticky?.destroy();
				window.removeEventListener('scroll', onFirstScroll, listenerOpts);
				if (mq?.removeEventListener) mq.removeEventListener('change', onViewportChange);
				else window.removeEventListener('resize', onViewportChange);
				window.removeEventListener('orientationchange', onViewportChange);
			};
		}

		if (typeof IntersectionObserver === 'undefined') {
			nearFooter = true;
			tryScheduleLoad();
			return () => {
				cancelDefer();
				gamSticky?.destroy();
			};
		}

		let observerDone = false;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.some((e) => e.isIntersecting);
				if (!visible || observerDone) return;
				observerDone = true;
				nearFooter = true;
				observer.disconnect();
				tryScheduleLoad();
			},
			{ root: null, rootMargin: FOOTER_AD_ROOT_MARGIN, threshold: 0 }
		);
		observer.observe(el);

		const mq =
			typeof window.matchMedia === 'function' ? window.matchMedia(MQ) : null;
		const onViewportChange = () => tryScheduleLoad();
		if (mq?.addEventListener) mq.addEventListener('change', onViewportChange);
		else window.addEventListener('resize', onViewportChange);
		window.addEventListener('orientationchange', onViewportChange);

		return () => {
			cancelDefer();
			gamSticky?.destroy();
			observer.disconnect();
			if (mq?.removeEventListener) mq.removeEventListener('change', onViewportChange);
			else window.removeEventListener('resize', onViewportChange);
			window.removeEventListener('orientationchange', onViewportChange);
		};
	}, [loadTrigger]);

	return (
		<div
			ref={rootRef}
			id="BANNER_FOOTER_FIXED"
			className="grumft-banner-footer-fixed"
		/>
	);
}
